const TAKE_BEFORE_CARET = 2000;

/** Append the end of `node`'s plain text (reverse document order) into `chunks`. */
const takeNodeEnd = (node, chunks, state) => {
  if (!node || state.need <= 0) return;
  if (node.nodeType === Node.TEXT_NODE) {
    const raw = node.textContent || '';
    const take = raw.length > state.need ? raw.slice(raw.length - state.need) : raw;
    if (take) chunks.push(take);
    state.need -= take.length;
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  if (node.tagName === 'BR') {
    chunks.push('\n');
    state.need -= 1;
    return;
  }
  for (let i = node.childNodes.length - 1; i >= 0 && state.need > 0; i -= 1) {
    takeNodeEnd(node.childNodes[i], chunks, state);
  }
};

const takeBefore = (node, root, chunks, state) => {
  let current = node;
  while (current && current !== root && state.need > 0) {
    let prev = current.previousSibling;
    while (prev && state.need > 0) {
      takeNodeEnd(prev, chunks, state);
      prev = prev.previousSibling;
    }
    current = current.parentNode;
  }
};

/**
 * Text immediately before the caret. Slash / `{{` detection only needs a
 * short suffix — `Range.toString()` on the whole field copies every
 * character and forces layout, which freezes after a large paste.
 */
export const getTextBeforeCaret = (root, maxChars = TAKE_BEFORE_CARET) => {
  if (!root) return '';
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !root.contains(sel.anchorNode)) return '';
  try {
    const range = sel.getRangeAt(0);
    const chunks = [];
    const state = { need: Number.isFinite(maxChars) ? maxChars : Number.MAX_SAFE_INTEGER };
    const node = range.startContainer;
    const offset = range.startOffset;

    if (node.nodeType === Node.TEXT_NODE) {
      const raw = (node.textContent || '').slice(0, Math.max(0, offset));
      const take = raw.length > state.need ? raw.slice(raw.length - state.need) : raw;
      if (take) chunks.push(take);
      state.need -= take.length;
      takeBefore(node, root, chunks, state);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const kids = node.childNodes;
      for (let i = Math.min(offset, kids.length) - 1; i >= 0 && state.need > 0; i -= 1) {
        takeNodeEnd(kids[i], chunks, state);
      }
      if (node !== root) takeBefore(node, root, chunks, state);
    }
    return chunks.reverse().join('');
  } catch {
    return '';
  }
};

/** Insert plain text at the caret as one text node (no per-line blocks). */
export const insertTextAtCaret = (text) => {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !text) return false;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};

export const placeCaretSafely = (sel, node, offset) => {
  if (!sel || !node) return;
  try {
    const next = document.createRange();
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.textContent?.length ?? 0;
      next.setStart(node, Math.max(0, Math.min(offset, len)));
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const len = node.childNodes.length;
      next.setStart(node, Math.max(0, Math.min(offset, len)));
    } else {
      return;
    }
    next.collapse(true);
    sel.removeAllRanges();
    sel.addRange(next);
  } catch {
    // Ignore caret restore failures — picker can still open.
  }
};

/** Delete `cut` characters immediately before the caret in the current text node. */
export const deleteCharsBeforeCaret = (cut) => {
  if (!cut) return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return false;

  const offset = range.startOffset;
  const text = node.textContent || '';
  if (offset < cut) return false;

  const newText = text.slice(0, offset - cut) + text.slice(offset);
  const newOffset = offset - cut;
  const parent = node.parentNode;
  node.textContent = newText;

  if (!parent) return true;

  if (newText.length === 0) {
    const index = Array.prototype.indexOf.call(parent.childNodes, node);
    node.remove();
    placeCaretSafely(sel, parent, Math.max(0, index));
  } else {
    placeCaretSafely(sel, node, newOffset);
  }
  return true;
};

export const createZwspMarker = (datasetKey) => {
  const marker = document.createElement('span');
  marker.dataset[datasetKey] = '1';
  marker.appendChild(document.createTextNode('\u200b'));
  return marker;
};

export const menuPositionFromMarker = (marker, el) => {
  const rect = marker.getBoundingClientRect();
  const fallback = el.getBoundingClientRect();
  return {
    x: rect.left || fallback.left,
    y: (rect.bottom || fallback.bottom) + 4,
    caretTop: rect.top || fallback.top,
  };
};
